import { useState, type RefObject } from 'react'
import * as Cesium from 'cesium'

export type VWorldMapType = 'Base' | 'Satellite' | 'Hybrid' | 'midnight'

const VWORLD_LAYERS: { key: VWorldMapType; label: string }[] = [
  { key: 'Base',      label: '기본지도' },
  { key: 'Satellite', label: '위성영상' },
  { key: 'Hybrid',    label: '하이브리드' },
  { key: 'midnight',  label: '야간지도' },
]

const API_KEY = import.meta.env.VITE_VWORLD_API_KEY ?? ''

// V-World WMTS REST URL prefix (GoogleMapsCompatible = EPSG:3857 tile matrix)
const VWORLD = (layer: string, ext: string) =>
  `https://api.vworld.kr/req/wmts/1.0.0/${API_KEY}/${layer}/{z}/{y}/{x}.${ext}`

const OSM_PROVIDER = () =>
  new Cesium.UrlTemplateImageryProvider({
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    credit: '© OpenStreetMap contributors',
  })

export function applyVWorldBasemap(viewer: Cesium.Viewer, mapType: VWorldMapType) {
  console.log(`Applying V-World basemap: ${mapType}`)
  viewer.imageryLayers.removeAll()

  // OSM을 하위 레이어로 추가 — V-World 미커버리지 영역(한국 외)에서 표시
  viewer.imageryLayers.addImageryProvider(OSM_PROVIDER())

  if (!API_KEY) return

  const credit = '© 공간정보 오픈플랫폼(V-World)'

  if (mapType === 'Hybrid') {
    // 위성영상 위에 도로·지명 레이어를 합성
    viewer.imageryLayers.addImageryProvider(
      new Cesium.UrlTemplateImageryProvider({ url: VWORLD('Satellite', 'jpeg'), credit, maximumLevel: 19 })
    )
    viewer.imageryLayers.addImageryProvider(
      new Cesium.UrlTemplateImageryProvider({ url: VWORLD('Hybrid', 'png'), maximumLevel: 19 })
    )
    return
  }

  const fmt = mapType === 'Satellite' ? 'jpeg' : 'png'
  viewer.imageryLayers.addImageryProvider(
    new Cesium.UrlTemplateImageryProvider({ url: VWORLD(mapType, fmt), credit, maximumLevel: 19 })
  )
}

interface Props {
  viewerRef: RefObject<Cesium.Viewer | null>
}

export default function VWorldBasemapSelector({ viewerRef }: Props) {
  const [selected, setSelected] = useState<VWorldMapType>('Base')

  function handleSelect(mapType: VWorldMapType) {
    const viewer = viewerRef.current
    if (!viewer || viewer.isDestroyed()) return
    setSelected(mapType)
    applyVWorldBasemap(viewer, mapType)
  }

  const attribution = API_KEY
    ? '© 공간정보 오픈플랫폼(V-World) · © OpenStreetMap contributors'
    : '© OpenStreetMap contributors'

  return (
    <>
      {/* 왼쪽 하단 출처 표기 */}
      <div
        className="absolute bottom-2 left-2 z-10 pointer-events-none select-none"
        style={{ fontSize: 11, color: 'rgba(0,0,0,0.7)', textShadow: '0 0 3px rgba(255,255,255,0.8)' }}
      >
        {attribution}
      </div>

      {/* 오른쪽 하단 배경지도 선택기 */}
      <div className="absolute bottom-6 right-4 z-10 select-none">
        <div className="bg-slate-900/90 backdrop-blur-sm rounded-lg shadow-xl overflow-hidden text-white" style={{ minWidth: 110 }}>
          <div className="px-3 py-1.5 bg-slate-800/60 border-b border-slate-700">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">배경지도</span>
          </div>
          <div className="p-1.5 flex flex-col gap-0.5">
            {VWORLD_LAYERS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleSelect(key)}
                className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-[11px] font-medium w-full text-left transition-colors ${
                  selected === key
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                {label}
                {selected === key && <span className="ml-auto text-[9px] text-blue-200">●</span>}
              </button>
            ))}
          </div>
          {!API_KEY && (
            <div className="px-3 pb-2 text-[9px] text-amber-400">
              API키 미설정 · OSM 사용중
            </div>
          )}
        </div>
      </div>
    </>
  )
}
